import React from 'react';
import { Package, Cpu, Lightbulb, Settings, Server } from 'lucide-react';
import { sanitizeSVG } from '@/lib/sanitize';
import { unescapeSvg, resizeSvg } from '@/lib/svgNormalizer';
import speakerSvg from '@/assets/icons/categories/speaker-transparent.svg';
import cameraSvg from '@/assets/icons/categories/camera.svg';
import wapSvg from '@/assets/icons/categories/wap.svg';
import touchPanelSvg from '@/assets/icons/categories/touchpanel.svg';
import doorbellSvg from '@/assets/icons/categories/doorbell.svg';
import thermostatSvg from '@/assets/icons/categories/thermostat.svg';
import dataSvg from '@/assets/icons/categories/data.svg';
import sensorSvg from '@/assets/icons/categories/sensor-cleaned.svg';
import shadesSvg from '@/assets/icons/categories/shades-cleaned.svg';

export type IconType = 'lucide' | 'svg' | 'emoji';

export interface CategoryIcon {
  type: IconType;
  content: React.ComponentType<any> | string;
}

// Map of category to icon configuration
export const categoryIcons: Record<string, CategoryIcon> = {
  // Using custom SVG
  speaker: { type: 'svg', content: speakerSvg },
  camera: { type: 'svg', content: cameraSvg },
  access_point: { type: 'svg', content: wapSvg },
  touch_panel: { type: 'svg', content: touchPanelSvg },
  doorbell: { type: 'svg', content: doorbellSvg },
  thermostat: { type: 'svg', content: thermostatSvg },
  data: { type: 'svg', content: dataSvg },
  sensor: { type: 'svg', content: sensorSvg },
  shades: { type: 'svg', content: shadesSvg },

  // Using same icons for related categories
  amplifier: { type: 'svg', content: speakerSvg }, // Using same speaker icon

  // Using Lucide icons for remaining categories
  processor: { type: 'lucide', content: Cpu },
  fixture: { type: 'lucide', content: Lightbulb },
  control: { type: 'lucide', content: Settings },
  group: { type: 'lucide', content: Server },

  // Default
  default: { type: 'lucide', content: Package },
};

export function getCategoryIcon(category: string): CategoryIcon {
  // Handle both hyphenated and underscored versions
  const normalizedCategory = category.replace(/-/g, '_');
  return categoryIcons[normalizedCategory] || categoryIcons.default;
}

interface CategoryIconRendererProps {
  category?: string;
  template?: {
    icon_svg?: string | null;
    icon_url?: string | null;
    default_color?: string | null;
    // Support for text/abbreviation icons
    icon_type?: 'svg' | 'abbreviation';
    abbreviation?: string;
    fill_color?: string;
    text_color?: string;
    icon_shape?: 'square' | 'circle';
  };
  className?: string;
  size?: number;
  strokeWidth?: number;
}

// unescapeSvg is now imported from @/lib/svgNormalizer

export function CategoryIconRenderer({
  category,
  template,
  className = '',
  size = 24,
  strokeWidth,
}: CategoryIconRendererProps) {
  // If template with abbreviation icon type is provided, render text icon
  if (template && template.icon_type === 'abbreviation' && template.abbreviation) {
    const fillColor = template.fill_color || '#6B7280';
    const textColor = template.text_color || '#FFFFFF';
    const isCircle = template.icon_shape === 'circle';
    const borderRadius = isCircle ? '50%' : '4px';

    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          backgroundColor: fillColor,
          borderRadius,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: textColor,
            fontSize: Math.max(8, size * 0.65),
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '-0.02em',
          }}
        >
          {template.abbreviation}
        </span>
      </div>
    );
  }

  // If template with custom SVG icon is provided, use it
  // SVGs are normalized at upload time, so we just apply CSS color here
  if (template && (template.icon_svg || template.icon_url)) {
    if (template.icon_svg) {
      const cleanedSvg = unescapeSvg(template.icon_svg);
      const resizedSvg = resizeSvg(cleanedSvg, size, size);
      const sanitizedSvg = sanitizeSVG(resizedSvg);

      return (
        <div
          className={className}
          style={{
            width: size,
            height: size,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: template.default_color || '#6B7280',
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
        />
      );
    }

    if (template.icon_url) {
      return (
        <img
          src={template.icon_url}
          alt="Category icon"
          style={{
            width: size,
            height: size,
            objectFit: 'contain',
          }}
        />
      );
    }
  }

  // Fall back to category-based icon
  if (!category) return null;
  const icon = getCategoryIcon(category);

  switch (icon.type) {
    case 'svg':
      return (
        <div
          className={className}
          style={{
            width: size,
            height: size,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
          }}
        >
          <img
            src={icon.content as string}
            alt={`${category} icon`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              backgroundColor: 'transparent',
              // Use CSS filter to make strokes slightly thicker while preserving colors
              filter: strokeWidth ? `drop-shadow(0 0 0.5px rgba(0,0,0,0.3))` : undefined,
            }}
            draggable={false}
            crossOrigin="anonymous"
          />
        </div>
      );
    case 'lucide': {
      const LucideIcon = icon.content as React.ComponentType<any>;
      return <LucideIcon className={className} size={size} strokeWidth={strokeWidth} />;
    }
    case 'emoji':
      return (
        <span className={className} style={{ fontSize: size }}>
          {icon.content}
        </span>
      );
    default:
      return null;
  }
}
